// @ts-check
/** @typedef {Object} FocusTrapOptions
 * @property {string | undefined} triggerElementQuery
 * @property {boolean} shouldTrapFocus
 */

/** @type {import('svelte/action').Action<HTMLElement, FocusTrapOptions>}  */
export const trapFocus = (node, options) => {
  /** @type {AbortController | undefined} */
  let abortController;
  let currentOptions = options;
  if (typeof window === "undefined") {
    return;
  }

  /** @type {HTMLElement} */
  let firstElement;
  /** @type {HTMLElement} */
  let secondElement;
  /** @type {HTMLElement} */
  let lastElement;

  const focusableElements =
    "a:not([tabindex='-1']), area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not([tabindex='-1']), iframe, object, embed, *[contenteditable]";

  /** @type {(e: KeyboardEvent) => void} */
  const onFirstElementKeydown = (e) => {
    if (!e.shiftKey && e.code === "Tab") {
      e.preventDefault();
      secondElement.focus();
    }
    if (e.shiftKey && e.code === "Tab") {
      e.preventDefault();
      lastElement.focus();
    }
  };

  /** @type {(e: KeyboardEvent) => void} */
  const onSecondElementKeydown = (e) => {
    if (e.shiftKey && e.code === "Tab") {
      e.preventDefault();
      firstElement.focus();
    }
  };

  /** @type {(e: KeyboardEvent) => void} */
  const onLastElementKeydown = (e) => {
    if (!e.shiftKey && e.code === "Tab") {
      e.preventDefault();
      firstElement.focus();
    }
  };

  const manageFocus = () => {
    if (currentOptions?.shouldTrapFocus !== true) {
      return;
    }

    abortController = new AbortController();
    const signal = abortController.signal;

    const focusableListList = node.querySelectorAll(focusableElements);

    const focusableList = Array.from(focusableListList);

    let triggerElement;
    if (currentOptions?.triggerElementQuery) {
      triggerElement = document.querySelector(options.triggerElementQuery);
    }

    if (triggerElement) {
      focusableList.unshift(triggerElement);
    }

    if (focusableList.length < 2) {
      return;
    }

    firstElement = /** @type {HTMLElement} */ (focusableList[0]);

    // For external trigger support
    secondElement = /** @type {HTMLElement} */ (focusableList[1]);

    lastElement = /** @type {HTMLElement} */ (focusableList[focusableList.length - 1]);

    firstElement?.addEventListener("keydown", onFirstElementKeydown, { signal });
    secondElement?.addEventListener("keydown", onSecondElementKeydown, { signal });
    lastElement?.addEventListener("keydown", onLastElementKeydown, { signal });
  };

  const cleanUp = () => {
    abortController?.abort();
  };

  const observationChange = () => {
    cleanUp();
    manageFocus();
  };

  const observer = new MutationObserver(observationChange);
  observer.observe(node, { childList: true, subtree: true });

  manageFocus();
  return {
    update(newOptions) {
      currentOptions = newOptions;
    },
    destroy() {
      observer.disconnect();
      cleanUp();
    }
  };
};
